import { toast } from '@heidi/design-system/toast';
import { useCallback } from 'react';
import type { IntlShape } from 'react-intl';

import type { SteerPendingApprovals } from '../../../data/projects/hooks/useSteerPendingApprovals';
import type { SteerableComposerTurn } from '../../../data/projects/lib/steerPendingApprovals';
import { steerTextForTurn } from '../../../data/projects/lib/steerPendingApprovals';
import type { ProjectChatEnqueueUx } from '../../../data/projects/turn-command/turn-command.machine';
import { COMPOSER_SEND_QUEUE_UX } from '../../../data/projects/turn-command/useQueuedTurns';

interface UseComposerApprovalSteerArgs<TTurn extends SteerableComposerTurn> {
  clearComposer: () => void;
  flashSendSuccess: () => void;
  /** Promote the turn to the front of the queue and stop the running turn. */
  handleSendNow: (turn: TTurn) => void;
  intl: IntlShape;
  onScrollTranscript?: () => void;
  onSteerPendingApprovals?: SteerPendingApprovals;
  /** Record the turn for the ArrowUp restore. */
  rememberLastSentTurn: (turn: TTurn) => void;
  /** The composer's ordinary send policy: queue behind a running turn, else dispatch. */
  sendOrQueueTurn: (turn: TTurn, ux?: ProjectChatEnqueueUx) => void;
  setDisableComposerForApprovalSteer: (disabled: boolean) => void;
}

/**
 * Sending while the agent waits on an approval steers it: the typed text
 * resolves the pending approval(s) as their ``reason`` and the turn resumes in
 * place. Returns the entry point the composer's ``onSend`` branches to.
 */
export const useComposerApprovalSteer = <TTurn extends SteerableComposerTurn>({
  clearComposer,
  flashSendSuccess,
  handleSendNow,
  intl,
  onScrollTranscript,
  onSteerPendingApprovals,
  rememberLastSentTurn,
  sendOrQueueTurn,
  setDisableComposerForApprovalSteer,
}: UseComposerApprovalSteerArgs<TTurn>): ((
  turn: TTurn,
  ux?: ProjectChatEnqueueUx
) => Promise<void>) => {
  // The message is on its way, whichever route it took. Follow the transcript;
  // remember / clear the composer when the caller's policy owns the draft.
  const acceptTurn = useCallback(
    (turn: TTurn, ux: ProjectChatEnqueueUx = COMPOSER_SEND_QUEUE_UX) => {
      if (ux.remember) rememberLastSentTurn(turn);
      onScrollTranscript?.();
      if (ux.clearComposer) clearComposer();
    },
    [clearComposer, onScrollTranscript, rememberLastSentTurn]
  );

  // Fallback for a draft a 500-char `reason` cannot carry faithfully — files, a
  // linked item, a skill, or past the cap. Reuses "Send now": the turn jumps the
  // queue, and the cancel settles the pending approval backend-side, writing a
  // proper tool result for the dangling tool_use so nothing is left hanging.
  const sendNowAheadOfPendingApproval = useCallback(
    (turn: TTurn, ux: ProjectChatEnqueueUx = COMPOSER_SEND_QUEUE_UX) => {
      acceptTurn(turn, ux);
      handleSendNow(turn);
    },
    [acceptTurn, handleSendNow]
  );

  return useCallback(
    async (turn: TTurn, ux: ProjectChatEnqueueUx = COMPOSER_SEND_QUEUE_UX) => {
      const steerText = steerTextForTurn(turn);
      if (!onSteerPendingApprovals || !steerText) {
        sendNowAheadOfPendingApproval(turn, ux);
        return;
      }

      setDisableComposerForApprovalSteer(true);
      let outcome: Awaited<ReturnType<SteerPendingApprovals>>;
      try {
        outcome = await onSteerPendingApprovals(steerText);
      } catch {
        outcome = 'failed';
      } finally {
        setDisableComposerForApprovalSteer(false);
      }

      // `partial` / `held` still landed the user's words; a clearComposer
      // policy must drop them so a normal send cannot double-send.
      if (outcome === 'steered' || outcome === 'partial' || outcome === 'held') {
        acceptTurn(turn, ux);
        flashSendSuccess();
        if (outcome === 'partial') {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Message sent',
              id: 'OzIqxn5jJT',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Heidi may still be waiting on another approval — check the conversation.',
              id: 'Dr432MvyMo',
            }),
          });
        }
        return;
      }
      // Nothing was there to steer — the approval resolved out from under us, or
      // the snapshot said "pending" while the inbox list was already empty. The
      // message is an ordinary one, so it takes the ordinary send policy;
      // cancelling here would kill the turn the resolved approval just resumed.
      if (outcome === 'gone' || outcome === 'unavailable') {
        sendOrQueueTurn(turn, ux);
        return;
      }
      toast({
        title: intl.formatMessage({
          defaultMessage: "Couldn't send message",
          id: 'xhw9Oa/914',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Please try again.',
          id: 'VjB59sDdPt',
        }),
        tone: 'destructive',
      });
    },
    [
      acceptTurn,
      flashSendSuccess,
      intl,
      onSteerPendingApprovals,
      sendNowAheadOfPendingApproval,
      sendOrQueueTurn,
      setDisableComposerForApprovalSteer,
    ]
  );
};
